'use client'

import { AnimatePresence, motion } from 'framer-motion'
import Image from 'next/image'
import Link from 'next/link'
import { useSearchParams } from 'next/navigation'
import { useState } from 'react'
import { Heading } from '@/components/heading'
import { cn, handleUETClick } from '@/lib/utils'
import { tiers, type Tier, type TierActionKind } from '@/constants/tier'

const appHost = process.env.NEXT_PUBLIC_APP_BASE_URL ?? 'https://app.aitubiao.com'

export function Pricing({ background }: { background: boolean }) {
  const searchParams = useSearchParams()
  const queryString = searchParams.toString()

  // 移动端按用户要求默认全部折叠；这里用 map 存每张卡的展开状态，允许用户单独展开任意套餐。
  const [expandedTiers, setExpandedTiers] = useState<Partial<Record<Tier['id'], boolean>>>({})
  const [advisorDialogOpen, setAdvisorDialogOpen] = useState(false)

  // 这里统一收口套餐 CTA 的跳转拼接，避免 query 透传和 openBuyPremium 参数散落到多个文件里。
  const buildHref = (kind: TierActionKind) => {
    if (kind === 'contact') {
      return '/about'
    }

    const extraQuery = kind === 'buy-vip' ? 'openBuyPremium=vip' : kind === 'buy-premium' ? 'openBuyPremium=premium' : ''

    if (!queryString && !extraQuery) {
      return `${appHost}/sign-in`
    }

    if (!queryString) {
      return `${appHost}/sign-in?${extraQuery}`
    }

    if (!extraQuery) {
      return `${appHost}/sign-in?${queryString}`
    }

    return `${appHost}/sign-in?${queryString}&${extraQuery}`
  }

  const toggleTier = (tierId: Tier['id']) => {
    setExpandedTiers(current => ({
      ...current,
      [tierId]: !current[tierId]
    }))
  }

  return (
    <section
      id="pricing"
      className={cn(
        'relative py-24',
        !background && 'md:py-28',
        background && 'bg-[radial-gradient(circle_at_top,rgba(104,94,246,0.08),transparent_55%)] pt-36'
      )}
    >
      <div className="mx-auto w-full max-w-[1440px] px-4 md:px-8">
        <Heading as="h2" className="mx-auto max-w-6xl text-center text-[30px] font-semibold leading-tight text-[#353745] md:text-5xl lg:text-6xl">
          产品定价
        </Heading>

        <div className="mx-auto mt-10 flex flex-col gap-4 md:hidden">
          {tiers.map(tier => (
            <MobileTierCard
              key={tier.id}
              tier={tier}
              href={buildHref(tier.action.kind)}
              expanded={Boolean(expandedTiers[tier.id])}
              onToggle={() => toggleTier(tier.id)}
              onOpenAdvisorDialog={() => setAdvisorDialogOpen(true)}
            />
          ))}
        </div>

        <div className="mx-auto mt-14 hidden max-w-[1380px] grid-cols-1 gap-6 md:grid md:grid-cols-2 xl:grid-cols-[repeat(4,268px)] xl:justify-center xl:gap-x-[60px]">
          {tiers.map(tier => (
            <TierCard key={tier.id} tier={tier} href={buildHref(tier.action.kind)} onOpenAdvisorDialog={() => setAdvisorDialogOpen(true)} />
          ))}
        </div>
      </div>

      <AdvisorQrModal open={advisorDialogOpen} onClose={() => setAdvisorDialogOpen(false)} />
    </section>
  )
}

function TierCard({ tier, href, onOpenAdvisorDialog }: { tier: Tier; href: string; onOpenAdvisorDialog: () => void }) {
  const isFeatured = tier.variant === 'featured'
  const isEnterprise = tier.variant === 'enterprise'

  return (
    <article
      className={cn(
        'flex min-h-[660px] flex-col rounded-[32px] border bg-white/80 px-7 py-8 backdrop-blur-[10px]',
        isFeatured && 'border-[1.5px] border-[#685EF6] bg-[linear-gradient(180deg,#FFFFFF_0%,#F8F6FF_100%)] shadow-[0_4px_10px_0_rgba(90,66,235,0.16)]',
        isEnterprise && 'rounded-[16px] border-[#C5CCFF] bg-white px-6 pb-8 backdrop-blur-none'
      )}
    >
      <div>
        <div className="relative">
          <h3 className={cn('text-center text-[30px] font-medium tracking-[0.08em] text-[#555974]', isEnterprise && 'text-[#353745]')}>{tier.name}</h3>

          {tier.subtitle ? (
            <p className="pointer-events-none absolute left-1/2 top-full mt-2 -translate-x-1/2 whitespace-nowrap text-center text-[13px] leading-[14px] text-[#555974]">
              {tier.subtitle}
            </p>
          ) : null}
        </div>

        <div className="mt-6 flex h-[52px] items-end justify-center text-center text-[#1F2233]">
          <div className={cn('text-[52px] font-semibold leading-none tracking-[-0.04em]', isEnterprise && 'text-[28px] leading-7 tracking-[0.05em]')}>
            {tier.priceMonthly}
            {tier.priceUnit ? <span className="ml-2 text-base font-medium tracking-normal text-[#555974]">{tier.priceUnit}</span> : null}
          </div>
        </div>

        <div className="mt-6 flex h-[52px] items-center justify-center" onClickCapture={handleUETClick}>
          {isEnterprise ? (
            <button type="button" onClick={onOpenAdvisorDialog} className={getTierActionClassName(tier.variant)}>
              {tier.action.label}
            </button>
          ) : (
            <Link
              href={href}
              className={getTierActionClassName(tier.variant)}
              style={{
                backgroundImage: isFeatured
                  ? 'radial-gradient(166.6% 143.01% at 7.75% 4.48%, #EBF1FF 4.75%, #F3FDFF 23.12%, #DEF7FF 48.08%, #F1FFF6 69.7%, #FFF 80.07%, #CDFFD4 98.05%)'
                  : undefined
              }}
            >
              {tier.action.label}
            </Link>
          )}
        </div>

        <p className="mt-6 border-b border-dashed border-[#C5CCFF] pb-8 text-center text-sm font-normal leading-[24px] text-[#555974]">{tier.description}</p>
      </div>

      <div className={cn('mt-6 flex flex-1 flex-col gap-4', isEnterprise && 'gap-4')}>
        {tier.features.map((feature, featureIndex) => (
          <div
            key={`${tier.id}-${featureIndex}`}
            className={cn('flex items-start gap-2.5 text-[15px] leading-6 text-[#353745]', isEnterprise && 'gap-1 text-[14px] leading-5')}
          >
            <div className="mt-0.5 h-5 w-5 shrink-0">{renderTierFeatureIcon(tier, feature.icon)}</div>
            <div className="min-w-0">{feature.description}</div>
          </div>
        ))}
      </div>
    </article>
  )
}

function MobileTierCard({
  tier,
  href,
  expanded,
  onToggle,
  onOpenAdvisorDialog
}: {
  tier: Tier
  href: string
  expanded: boolean
  onToggle: () => void
  onOpenAdvisorDialog: () => void
}) {
  const isFeatured = tier.variant === 'featured'
  const isEnterprise = tier.variant === 'enterprise'

  // Figma 的移动端免费版这里单独用了“即刻体验”和“&”文案，桌面端不跟着改。
  const mobileActionLabel = tier.id === 'free' ? '即刻体验' : tier.action.label
  const mobileDescription = tier.id === 'free' ? '满足基础使用&体验需求' : tier.description

  return (
    <article
      className={cn(
        'overflow-hidden rounded-[16px] border bg-white/80',
        isFeatured && 'border-[1.5px] border-[#685EF6] shadow-[0_4px_10px_0_rgba(90,66,235,0.16)]',
        !isFeatured && 'border-[#C5CCFF]'
      )}
    >
      <div className="px-5 pb-4 pt-5">
        <div className="flex flex-col items-center text-center">
          <h3 className="text-[20px] font-medium tracking-[0.08em] text-[#353745]">{tier.name}</h3>

          {tier.subtitle ? <p className="mt-2 text-[13px] leading-[1.4] text-[#555974]">{tier.subtitle}</p> : null}

          <div className={cn('mt-4 flex items-end justify-center gap-1 text-[#353745]', isEnterprise && 'mt-3')}>
            <span className={cn('text-[28px] font-semibold leading-none', isEnterprise && 'text-[22px] leading-[1.2]')}>{tier.priceMonthly}</span>
            {tier.priceUnit ? <span className="pb-0.5 text-sm leading-none text-[#555974]">{tier.priceUnit}</span> : null}
          </div>
        </div>

        <div className="mt-4" onClickCapture={handleUETClick}>
          {isEnterprise ? (
            <button type="button" onClick={onOpenAdvisorDialog} className={getMobileTierActionClassName(tier.variant)}>
              {mobileActionLabel}
            </button>
          ) : (
            <Link
              href={href}
              className={getMobileTierActionClassName(tier.variant)}
              style={{
                backgroundImage: isFeatured
                  ? 'radial-gradient(166.6% 143.01% at 7.75% 4.48%, #EBF1FF 4.75%, #F3FDFF 23.12%, #DEF7FF 48.08%, #F1FFF6 69.7%, #FFF 80.07%, #CDFFD4 98.05%)'
                  : undefined
              }}
            >
              {mobileActionLabel}
            </Link>
          )}
        </div>

        <button
          type="button"
          onClick={onToggle}
          aria-expanded={expanded}
          className="mt-4 flex w-full items-center justify-between rounded-[8px] bg-[#F6F7F9] px-4 py-3 text-left"
        >
          <span className="min-w-0 pr-4 text-[12px] leading-4 text-[#555974]">{mobileDescription}</span>
          <ChevronIcon className={cn('h-4 w-4 shrink-0 text-[#8A8FA8] transition duration-200', expanded && 'rotate-180')} />
        </button>
      </div>

      <AnimatePresence initial={false}>
        {expanded ? (
          <motion.div
            initial={{ height: 0, opacity: 0 }}
            animate={{ height: 'auto', opacity: 1 }}
            exit={{ height: 0, opacity: 0 }}
            transition={{ duration: 0.22, ease: 'easeOut' }}
            className="overflow-hidden"
          >
            <div className="border-t border-dashed border-[#D8DEF5] px-5 pb-5 pt-4">
              <div className="flex flex-col gap-3.5">
                {tier.features.map((feature, featureIndex) => (
                  <div key={`${tier.id}-mobile-${featureIndex}`} className="flex items-start gap-2 text-[13px] leading-5 text-[#353745]">
                    <div className="mt-0.5 h-5 w-5 shrink-0">{renderTierFeatureIcon(tier, feature.icon)}</div>
                    <div className="min-w-0">{feature.description}</div>
                  </div>
                ))}
              </div>
            </div>
          </motion.div>
        ) : null}
      </AnimatePresence>
    </article>
  )
}

function AdvisorQrModal({ open, onClose }: { open: boolean; onClose: () => void }) {
  return (
    <AnimatePresence>
      {open ? (
        <motion.div
          initial={{ opacity: 0 }}
          animate={{ opacity: 1 }}
          exit={{ opacity: 0 }}
          transition={{ duration: 0.2, ease: 'easeOut' }}
          className="fixed inset-0 z-[80] flex items-center justify-center bg-[rgba(31,34,51,0.76)] px-4 py-6"
          onClick={onClose}
        >
          <motion.div
            initial={{ y: 16, opacity: 0, scale: 0.96 }}
            animate={{ y: 0, opacity: 1, scale: 1 }}
            exit={{ y: 16, opacity: 0, scale: 0.96 }}
            transition={{ duration: 0.22, ease: 'easeOut' }}
            className="relative h-[298px] w-[326px] max-w-[calc(100vw-2rem)] rounded-[16px] bg-white p-4 shadow-[0_24px_80px_rgba(31,34,51,0.18)]"
            onClick={event => event.stopPropagation()}
          >
            <button
              type="button"
              onClick={onClose}
              aria-label="关闭弹窗"
              className="absolute right-4 top-4 flex h-6 w-6 items-center justify-center text-[#B1B6CB] transition duration-200 hover:text-[#7C75FF]"
            >
              <CloseIcon className="h-4 w-4" />
            </button>

            {/* 按用户给定尺寸收口：弹窗 326×298，圆角 16，内边距 16，内容居中，二维码 block 160。 */}
            <div className="flex h-full flex-col items-center justify-center gap-4">
              <h3 className="text-center text-[14px] font-semibold leading-[1.5] text-[#353745]">扫码添加客服微信，了解定制方案</h3>

              <div
                className="flex h-[160px] w-[160px] items-center justify-center rounded-[8px] border border-[#A1D7E5] p-[10px]"
                style={{ background: 'linear-gradient(184deg, rgba(255, 255, 255, 0.00) -104.1%, #B4EAFC 116.78%)' }}
              >
                <Image
                  src="https://cdn.aitubiao.com/static/images/itubiao/qrcode/landingpage-business.png"
                  alt="企业版咨询顾问二维码"
                  width={140}
                  height={140}
                  className="block h-full w-full object-contain"
                />
              </div>
            </div>
          </motion.div>
        </motion.div>
      ) : null}
    </AnimatePresence>
  )
}

function renderTierFeatureIcon(tier: Tier, icon: Tier['features'][number]['icon']) {
  if (icon !== true) {
    return icon
  }

  return tier.variant === 'enterprise' ? <EnterpriseActiveIcon /> : <ActiveIcon />
}

function getTierActionClassName(variant: Tier['variant']) {
  if (variant === 'featured') {
    return 'inline-flex h-[52px] min-w-[128px] items-center justify-center rounded-full border border-[#685EF6] bg-transparent px-7 text-sm font-medium text-black transition duration-200 hover:-translate-y-0.5'
  }

  if (variant === 'enterprise') {
    return 'inline-flex h-[52px] min-w-[128px] items-center justify-center rounded-full border border-[#685EF6] bg-[rgba(90,66,235,0.08)] px-7 text-sm font-medium text-black transition duration-200 hover:-translate-y-0.5 hover:bg-[rgba(90,66,235,0.12)]'
  }

  return 'inline-flex h-[52px] min-w-[128px] items-center justify-center rounded-full border border-[#C9CEF7] bg-[#EDF0FF] px-7 text-sm font-medium text-[#353745] transition duration-200 hover:-translate-y-0.5 hover:border-[#685EF6] hover:text-[#493FD1]'
}

function getMobileTierActionClassName(variant: Tier['variant']) {
  if (variant === 'featured') {
    return 'inline-flex h-[58px] w-full items-center justify-center rounded-full border border-[#685EF6] px-4 text-[18px] font-medium text-black transition duration-200 hover:-translate-y-0.5'
  }

  return 'inline-flex h-[58px] w-full items-center justify-center rounded-full border border-[#685EF6] bg-[rgba(90,66,235,0.08)] px-4 text-[18px] font-medium text-black transition duration-200 hover:-translate-y-0.5 hover:bg-[rgba(90,66,235,0.12)]'
}

const ActiveIcon = () => {
  return (
    <svg xmlns="http://www.w3.org/2000/svg" width="20" height="21" viewBox="0 0 20 21" fill="none" aria-hidden="true">
      <path
        d="M10 18.988C12.3012 18.988 14.3845 18.0552 15.8926 16.5472C17.4006 15.0391 18.3334 12.9558 18.3334 10.6546C18.3334 8.35346 17.4006 6.27012 15.8926 4.76206C14.3845 3.25403 12.3012 2.32129 10 2.32129C7.69885 2.32129 5.61552 3.25403 4.10746 4.76206C2.59943 6.27012 1.66669 8.35346 1.66669 10.6546C1.66669 12.9558 2.59943 15.0391 4.10746 16.5472C5.61552 18.0552 7.69885 18.988 10 18.988Z"
        fill="#5A42EB"
        fillOpacity="0.08"
      />
      <path d="M6.66669 10.6548L9.16669 13.1548L14.1667 8.15479" stroke="#8A8FA8" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" />
    </svg>
  )
}

const EnterpriseActiveIcon = () => {
  return (
    <svg xmlns="http://www.w3.org/2000/svg" width="20" height="20" viewBox="0 0 20 20" fill="none" aria-hidden="true">
      <circle cx="10" cy="10" r="8" fill="#F1F2FA" />
      <path d="M7.16669 10L9.16669 12L12.8334 8.33331" stroke="#B7BBCD" strokeWidth="1.5" strokeLinecap="round" strokeLinejoin="round" />
    </svg>
  )
}

const ChevronIcon = ({ className }: { className?: string }) => {
  return (
    <svg xmlns="http://www.w3.org/2000/svg" width="16" height="16" viewBox="0 0 16 16" fill="none" aria-hidden="true" className={className}>
      <path d="M4.5 6.5L8 10L11.5 6.5" stroke="currentColor" strokeWidth="1.5" strokeLinecap="round" strokeLinejoin="round" />
    </svg>
  )
}

const CloseIcon = ({ className }: { className?: string }) => {
  return (
    <svg xmlns="http://www.w3.org/2000/svg" width="16" height="16" viewBox="0 0 16 16" fill="none" aria-hidden="true" className={className}>
      <path d="M4 4L12 12" stroke="currentColor" strokeWidth="1.5" strokeLinecap="round" />
      <path d="M12 4L4 12" stroke="currentColor" strokeWidth="1.5" strokeLinecap="round" />
    </svg>
  )
}
